import { DOCUMENT } from '@angular/common';
import {
  Component,
  ElementRef,
  HostBinding,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Renderer2,
  ViewChild,
} from '@angular/core';
import { MatButton } from '@angular/material/button';
import { Subject, Subscription } from 'rxjs';
import { filter, finalize, first, takeUntil } from 'rxjs/operators';

import { AlcmeonContextService } from 'src/app/core/alcmeon-context/alcmeon-context.service';
import { SelectTagComponent } from 'src/app/shared/components/select-tags/select-tag/select-tag.component';
import { SiteV2 } from 'src/app/shared/models';
import { Site, TagGroup, TaskState } from 'src/app/shared/models/misc.model';
import { SendByEmailSettings } from 'src/app/shared/models/send-by-mail';
import { BasicService } from 'src/app/shared/services/basic.service';
import { SendConversationByEmailService } from 'src/app/shared/services/send-by-email.service';
import { TrustpilotService } from 'src/app/shared/strategies/channels';
import { ModalDismissRoleEnum } from 'src/app/task-process/answer-editor/send-and-popover/modal-dismiss-role.enum';
import { environment } from 'src/environments/environment';
import { ActionButtonConfig } from '../../shared/action-button-config';
import { SelectModerationActionPopoverComponent } from '../../shared/components/select-moderation-action-popover/select-moderation-action-popover.component';
import { AnnotationsModalComponent } from '../annotations-modal/annotations-modal.component';
import { HelpRequestsModalComponent } from '../help-requests-modal/help-requests-modal.component';
import { TaskPublicContext } from '../public-context-and-task-actions/public-context/task-public-context';
import { taskIdFromTaskOrActionId } from '../shared/id-conversions';
import { AnnotationOrHelpAction, AnyTaskOrActionType, TaskOrActionType } from '../shared/model';
import { TagsContext } from '../shared/tags-context.class';
import { TaskProcessContext } from '../shared/task-process-context.class';
import { TaskProcessModalsService } from '../shared/task-process-modals.service';
import { TaskProcessUpdatesService } from '../shared/task-process-updates.service';
import { TaskProcessService } from '../shared/task-process.service';
import { ArchiveAndOtherActionsService } from './archive-and-other-actions.service';
import { ConfirmAnonymizeComponent } from './confirm-anonymize/confirm-anonymize.component';
import { SendByEmailPopoverComponent } from './send-by-email-popover/send-by-email-popover.component';

@Component({
  selector: 'app-archive-and-other-actions',
  templateUrl: './archive-and-other-actions.component.html',
  styleUrls: ['./archive-and-other-actions.component.scss'],
})
export class ArchiveAndOtherActionsComponent implements OnInit, OnDestroy {
  @Input() taskProcessContext: TaskProcessContext;
  @Input() processedTask: any;
  @Input() tagsContext: TagsContext;

  @ViewChild('selectRoutingTag') selectRoutingTag: SelectTagComponent;
  @ViewChild('sendByEmailReOpenButton', { read: ElementRef }) sendByEmailReOpenButton: ElementRef<MatButton>;

  taskPublicContext: TaskPublicContext;
  buttonConfigs: ActionButtonConfig[] = [];
  routeButtonConfig: ActionButtonConfig;
  shouldShowBackButton: boolean;
  routingTagGroup: TagGroup;
  showSendByEmailReopenButton = false;

  private subscriptions = new Subscription();
  private annotations: AnnotationOrHelpAction[] = [];
  private helpReqsAndAnswers: AnnotationOrHelpAction[] = [];
  private annotationsCount$ = new Subject<number>(); // displayed in the red badge
  private helpReqsAndAnswersCount$ = new Subject<number>(); // displayed in the red badge

  constructor(
    private basicService: BasicService,
    private taskProcessService: TaskProcessService,
    private archiveAndOtherActionsService: ArchiveAndOtherActionsService,
    private taskProcessModalsService: TaskProcessModalsService,
    private taskProcessUpdatesService: TaskProcessUpdatesService,
    private alcmeonContextService: AlcmeonContextService,
    private sendConversationByEmailService: SendConversationByEmailService,
    private renderer: Renderer2,
    @Inject(DOCUMENT) public document: Document,
    private trustpilotService: TrustpilotService
  ) {}

  @HostBinding('class.mandatory-and-missing-routing-tag') get hasError() {
    return this.routingTagGroup?.isMandatoryAndMissing;
  }

  ngOnInit() {
    this.initButtons();
    this.routingTagGroup = this.tagsContext.getRoutingTagGroup();
    this.subscriptions.add(
      this.taskProcessContext.isProcessedTaskLoaded$
        .pipe(first(Boolean))
        .subscribe(() => this.initAnnotationsAndHelpActions())
    );
  }

  async onReleaseAndBackClicked(event: MouseEvent) {
    await this.taskProcessContext.backToTaskListRequested(event);
  }

  onArchiveClicked() {
    this.basicService.showSpinner();
    this.archiveAndOtherActionsService
      .archiveTask(this.taskProcessContext.processedTaskId)
      .pipe(finalize(() => this.basicService.hideSpinner()))
      .subscribe(() => this.taskProcessContext.backToTaskList());
  }

  private initAnnotationsAndHelpActions() {
    // Preload existing annotations and help requests/answers
    const annotationIds = this.taskProcessContext.allAnnotationIds;
    const helpRequestAndAnswerIds = this.taskProcessContext.allHelpRequestAndResponseIds;
    this.taskProcessService
      .lazyloadTasksForProcessing([...annotationIds, ...helpRequestAndAnswerIds])
      .subscribe(response => {
        this.insertAnnotationsOrHelpActions(
          TaskOrActionType.Annotation,
          annotationIds.map(id => response.tasksOrActionsById[id]) as AnnotationOrHelpAction[]
        );
        this.insertAnnotationsOrHelpActions(
          TaskOrActionType.HelpRequest,
          helpRequestAndAnswerIds.map(id => response.tasksOrActionsById[id]) as AnnotationOrHelpAction[]
        );

        if (this.shouldOpenHelpRequestRightPanel()) {
          setTimeout(() => this.onHelpRequestButtonClicked(), 500);
        }
        // Update our lists when the backend notifies us that there is a new one
        this.subscribeToNewTaskOrActions();
      });
  }

  private subscribeToNewTaskOrActions() {
    this.subscriptions.add(
      this.taskProcessUpdatesService.newTaskOrAction$
        .pipe(filter(isAnnotationOrHelpAction), takeUntil(this.taskProcessContext.ngOnDestroy$))
        .subscribe(newTaskOrAction =>
          this.insertAnnotationsOrHelpActions(newTaskOrAction.type, [{ ...newTaskOrAction }])
        )
    );
    // we clone the action because setUIProperty will modify it
    // (the day indicators will be different than on the main conversation bubbles)
  }

  private insertAnnotationsOrHelpActions(type: TaskOrActionType, newActions: AnnotationOrHelpAction[]) {
    const currentActions = type === TaskOrActionType.Annotation ? this.annotations : this.helpReqsAndAnswers;
    const currentLatest = currentActions.length > 0 ? currentActions[currentActions.length - 1] : null;
    this.taskProcessService.setUIProperties(newActions, currentLatest);
    currentActions.push(...newActions);
    /*
     * Allows to update the task process context with the actual list of requests and answers.
     * This list is actually not updated after a event triggered from a ws.
     */
    if (type !== TaskOrActionType.Annotation) {
      const ids: string[] = newActions.map(({ id }) => id);
      this.taskProcessContext.allHelpRequestAndResponseIds.push(...ids);
    }
    this.updateBadges();
  }

  private updateBadges() {
    this.annotationsCount$.next(this.annotations.length);
    this.helpReqsAndAnswersCount$.next(this.helpReqsAndAnswers.length);
  }

  private initButtons() {
    const moderateActionButtonConfig = new ActionButtonConfig('m/task-process/tooltips/moderate', null, event => {
      this.onModerateClicked(event);
    });
    const siteType = this.taskProcessContext.site.type;
    moderateActionButtonConfig.iconUrl = `assets/img/site_icon/${siteType}.svg`;
    const canHaveModerationActionFn = () =>
      !this.archiveAndOtherActionsService.canHaveModerationAction(
        this.taskProcessContext.isPublic(),
        (this.taskProcessContext.site as SiteV2).typeName || (this.taskProcessContext.site as Site).type
      );
    moderateActionButtonConfig.isDisabledFn = canHaveModerationActionFn;
    moderateActionButtonConfig.hideFn = () => this.processedTask.isDeleted;

    if (!this.taskProcessContext.isCRMMode) {
      const isReadOnlyFn = () => this.taskProcessContext.isReadOnlyMode;

      const archive = new ActionButtonConfig('m/task-process/tooltips/archive', 'archive', () =>
        this.onArchiveClicked()
      );
      archive.isDisabledFn = isReadOnlyFn;

      this.routeButtonConfig = new ActionButtonConfig('m/task-process/tooltips/route', 'people', event =>
        this.onRouteClicked(event)
      );
      this.routeButtonConfig.isDisabledFn = isReadOnlyFn;

      const help = new ActionButtonConfig('m/task-process/tooltips/help-requests', 'help-circle', () => {
        this.onHelpRequestButtonClicked();
      });
      help.badge$ = this.helpReqsAndAnswersCount$;
      help.isDisabledFn = isReadOnlyFn;

      const anno = new ActionButtonConfig('m/task-process/tooltips/annotations', 'alert-circle', () => {
        this.onAnnotateButtonClicked();
      });
      anno.badge$ = this.annotationsCount$;

      this.buttonConfigs = [archive, this.routeButtonConfig, help, anno, moderateActionButtonConfig];

      if (this.alcmeonContextService.isLoggedUserAdmin() && !this.processedTask.isAnonymized) {
        // do not show button for non admin or if msg is already anonymized
        const anonymizeMessage = new ActionButtonConfig(
          'm/task-process/tooltips/anonymize-message',
          'eye-off',
          event => {
            this.onAnonymizeMessageClicked(event);
          }
        );
        this.buttonConfigs.push(anonymizeMessage);
      }
    } else if (this.trustpilotService.getTypeString() === siteType) {
      this.buttonConfigs = [moderateActionButtonConfig];
    }

    /*
     * do not show "send by email" icon if :
     * - in mobile
     * - no "send by email" config or config not enabled
     */
    if (environment.bundledWithAngularJSApp) {
      this.showSendByEmailReopenButton = true;
      this.sendConversationByEmailService.getSettings().subscribe((resp: SendByEmailSettings[]) => {
        const settingsEnabled = resp?.find(settings => settings.enabled);

        if (settingsEnabled) {
          const sendByEmail = new ActionButtonConfig('m/task-process/tooltips/send-by-email', null, event => {
            this.openSendByEmailPopover(event, settingsEnabled);
          });
          sendByEmail.iconUrl = `assets/img/reply_all_right.svg`;
          this.buttonConfigs.push(sendByEmail);
        }
      });
    }

    const { isPushboxMode } = this.taskProcessContext;
    const { previousStateCode } = this.taskProcessContext.processedTaskInfo.state;
    const showInPushBox = !previousStateCode || [TaskState.OnHold, TaskState.HelpRequested].includes(previousStateCode);

    this.shouldShowBackButton = environment.bundledWithAngularJSApp && (!isPushboxMode || showInPushBox);
  }

  private async openSendByEmailPopover(event, settings: SendByEmailSettings) {
    const modalInput = {
      processedTask: this.processedTask,
      reOpenButton: this.sendByEmailReOpenButton,
      settings,
      siteId: this.taskProcessContext.site.id,
      userName: this.taskProcessContext.from.name,
    };

    const modal = this.document.getElementsByClassName('send-by-email-modal');
    if (modal.length) {
      this.renderer.removeClass(modal[0], 'hidden');
      this.renderer.addClass(this.sendByEmailReOpenButton.nativeElement, 'hidden');
    } else {
      await this.basicService.modal(
        {
          component: SendByEmailPopoverComponent,
          componentProps: { ...modalInput },
          cssClass: 'send-by-email-modal',
        },
        true
      );
    }
  }

  reOpenSendByEmailPopover() {
    this.renderer.removeClass(this.document.getElementsByClassName('send-by-email-modal')[0], 'hidden');
    this.renderer.addClass(this.sendByEmailReOpenButton.nativeElement, 'hidden');
  }

  private onRouteClicked(event) {
    this.selectRoutingTag.open(event);
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  private async onAnonymizeMessageClicked(event) {
    const modalResult = await this.basicService.popoverWithResult(null, ConfirmAnonymizeComponent);
    if (modalResult.role === ModalDismissRoleEnum.OK) {
      this.taskProcessService.anonymizeMessage(this.taskProcessContext.processedTaskId).subscribe(() => {
        this.basicService.toastSuccess('m/task-process/moderation-action/sucess');
      });
    }
  }

  private async onAnnotateButtonClicked() {
    const modalInput = {
      taskId: this.taskProcessContext.processedTaskId,
      annotations: this.annotations,
      taskProcessContext: this.taskProcessContext,
    };
    await this.taskProcessModalsService.showTaskProcessPageModal(AnnotationsModalComponent, modalInput);
  }

  private async onHelpRequestButtonClicked() {
    const modalInput = {
      taskId: this.taskProcessContext.processedTaskId,
      helpReqsAndAnswers: this.helpReqsAndAnswers,
      taskProcessContext: this.taskProcessContext,
    };
    const modal = await this.taskProcessModalsService.showTaskProcessPageModal(HelpRequestsModalComponent, modalInput);
    const result = await modal.onDidDismiss();
    // In case of help request or answer btn click inside modal go back to taskList after dismissal
    if (result.data) {
      return this.taskProcessContext.backToTaskList(result.data.queueId);
    }
  }

  private async onModerateClicked(event) {
    const componentProps = {
      messageId: this.taskProcessContext.processedTaskInfo.messageId,
      siteId: this.taskProcessContext.site.id,
    };

    const modalResult = await this.basicService.popoverWithResult(
      event,
      SelectModerationActionPopoverComponent,
      componentProps
    );
    if (modalResult.role === ModalDismissRoleEnum.OK) {
      this.onModerateActionSelected(modalResult.data.actionType);
    }
  }

  private onModerateActionSelected(actionType: string) {
    const messageId = this.taskProcessContext.processedTaskInfo.messageId;
    const siteId = this.taskProcessContext.site.id;
    const taskId = taskIdFromTaskOrActionId(this.taskProcessContext.processedTaskInfo.id);
    this.archiveAndOtherActionsService.performModerationAction(siteId, taskId, messageId, actionType).subscribe(() => {
      this.basicService.toastSuccess('m/task-process/moderation-action/sucess');
    });
  }

  private shouldOpenHelpRequestRightPanel(): boolean {
    if (this.taskProcessContext?.processedTaskInfo?.state) {
      const needsHelp = (taskState: TaskState) => [TaskState.HelpAnswered, TaskState.HelpRequested].includes(taskState);
      const { taskStateCode, previousStateCode } = this.taskProcessContext.processedTaskInfo.state;
      return needsHelp(taskStateCode) || needsHelp(previousStateCode);
    }
    return false;
  }

  ngOnDestroy() {
    this.subscriptions?.unsubscribe();
  }
}

/**
 * helper function outside of class so that we can use its type predicate signature
 * in a .filter() to narrow the observable's type
 */
function isAnnotationOrHelpAction(taskOrAction: AnyTaskOrActionType): taskOrAction is AnnotationOrHelpAction {
  const interestingTypes = [TaskOrActionType.Annotation, TaskOrActionType.HelpRequest, TaskOrActionType.HelpAnswer];
  return interestingTypes.includes(taskOrAction.type);
}
